import { useParams, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  isBitcoinAddress,
  isEthereumAddress,
  isTronAddress,
  isEthereumTransactionHash,
  isBitcoinTransactionHash,
  isTronTransactionHash,
} from 'src/utils/crypto';
import { TBlockchain } from 'src/types';
import { EVMTransaction } from '../Transaction/Ethereum/EVMTransaction.page';
import { EVMAddressPage } from './EvmChains/EVMAddress.page';
import { TxHash } from '../Transaction/Bitcoin/TxHash.page';
import { BitcoinAddress } from './Bitcoin/BitcoinAddress.page';
// Map of supported chains and their validators
const CHAIN_ADDR_VALIDATORS: Record<TBlockchain, (id: string) => boolean> = {
  bitcoin: isBitcoinAddress,
  ethereum: isEthereumAddress,
  tron: isTronAddress,

  // TODO: Add validators for arbitrum and base
  arbitrum: isEthereumAddress,
  base: isEthereumAddress,
};

// Map of supported chains and their transaction hash validators
const CHAIN_TX_HASH_VALIDATORS: Record<TBlockchain, (hash: string) => boolean> =
  {
    bitcoin: isBitcoinTransactionHash,
    ethereum: isEthereumTransactionHash,
    tron: isTronTransactionHash,

    // TODO: Add validators for arbitrum and base
    arbitrum: isEthereumTransactionHash,
    base: isEthereumTransactionHash,
  };

type TPageType = 'address' | 'transaction';

export const ChainRouter = () => {
  const {
    chain,
    id: addrOrTxHash,
    type,
  } = useParams<{
    chain: TBlockchain;
    type: TPageType;
    id: string;
  }>();
  // chainUrl is the chain name from the url, TBlockchain is uppercase
  if (!chain || !addrOrTxHash || !type) {
    return <Navigate to="/" replace />;
  }

  // Check if it's a transaction hash for the specified chain
  const txHashValidator = CHAIN_TX_HASH_VALIDATORS[chain];
  if (txHashValidator && txHashValidator(addrOrTxHash)) {
    if (chain === 'ethereum') {
      return <EVMTransaction />;
    } else if (chain === 'bitcoin') {
      return <TxHash />;
    } else if (chain === 'tron') {
      return <Loader2 />;
    }
  }

  // Check if it's a valid address for the specified chain
  const addressValidator = CHAIN_ADDR_VALIDATORS[chain];
  if (!addressValidator) {
    return <Navigate to={`/${chain}/unknown/${addrOrTxHash}`} replace />;
  }

  const isValid = addressValidator(addrOrTxHash);
  if (!isValid) {
    return <Navigate to={`/${chain}/unknown/${addrOrTxHash}`} replace />;
  }

  if (chain === 'bitcoin') {
    if (type === 'address') {
      return <BitcoinAddress />;
    } else if (type === 'transaction') {
      return <TxHash />;
    }
  }

  // EVM address page
  return <EVMAddressPage />;
};
